import type { TranslationShape } from '../types';

export const fr = {
  common: {
    loading: 'Chargement...',
    initializing: 'Initialisation...',
    continue: 'Continuer',
    back: 'Retour',
    retry: 'Réessayer',
    tryAgain: 'Réessayer',
    goBack: 'Retour',
    cancel: 'Annuler',
    close: 'Fermer',
    closeModal: 'Fermer la fenêtre',
    closeWindow: 'Fermer la fenêtre',
    startOver: 'Recommencer',
    switchToMobile: 'Passer sur mobile',
    enableCamera: 'Activer la caméra',
    returnToSite: 'Retour au site',
    notice: 'Avis',
    done: 'Terminé',
    saving: 'Enregistrement...',
    processing: 'Traitement...',
    error: 'Erreur',
    moreInfo: "Plus d'informations",
    productFallback: 'vérification',
  },
  ageEstimateUnavailable: {
    title: 'Nous n’avons pas pu vérifier votre âge pour le moment',
    description:
      'Un problème est survenu de notre côté lors de l’analyse de votre selfie. Vous pouvez réessayer ou vous vérifier avec votre pièce d’identité en utilisant le selfie que vous venez de prendre.',
    descriptionIdOnly:
      'Un problème est survenu de notre côté lors de l’analyse de votre selfie. Vous pouvez continuer en vous vérifiant avec votre pièce d’identité et le selfie que vous venez de prendre.',
    useId: 'Vérifier avec une pièce d’identité',
  },
  pipelineRecovery: {
    title: 'La vérification a été temporairement interrompue',
    description:
      'Cette étape ne peut pas continuer pour le moment. Votre vérification reste active.',
    retrying: 'Nous réessayons automatiquement. Gardez cette page ouverte.',
    readyToRetry: 'Réessayez lorsque vous êtes prêt.',
    retryingAction: 'Nouvelle tentative…',
  },
  lifecycleRecovery: {
    loadTitle: 'Nous n’avons pas pu charger l’étape suivante', loadDescription: 'Vérifiez votre connexion et réessayez.', retryTitle: 'Essayons à nouveau',
    retryDescription: 'Cette étape a été interrompue. Veuillez réessayer.', retrySelfieTitle: 'Reprenons votre selfie', retrySelfieDescription: 'Placez-vous face à la caméra dans un endroit bien éclairé, puis réessayez.', retryIdTitle: 'Reprenons une photo de votre pièce d’identité', retryIdDescription: 'Assurez-vous que toute votre pièce d’identité est visible et lisible.', restartStepTitle: 'Reprenons cette étape',
    restartStepDescription: 'Nous devons reprendre cette étape pour continuer.', startAgain: 'Recommencer', beginAgain: 'Tout recommencer',
    cannotContinueTitle: 'Cette vérification ne peut pas continuer', cannotContinueDescription: 'Commencez une nouvelle vérification.', waitingTitle: 'Nous terminons cette étape…',
    waitingDescription: 'Gardez cette page ouverte. Nous continuerons automatiquement.', cancelledTitle: 'Vérification annulée', completeTitle: 'Vérification terminée',
    closeDescription: 'Vous pouvez fermer cette fenêtre.', returnDescription: 'Vous pouvez retourner sur le site.', continueTitle: 'Prêt à continuer', continueDescription: 'Passez à l’étape suivante.',
    rateLimitedTitle: 'Nous avons besoin d’un instant', rateLimitedDescription: 'Nous ne pouvons pas vérifier l’étape suivante pour le moment. Votre vérification reste active.', checkStatus: 'Vérifier le statut',
    finalizingTitle: 'Finalisation de votre vérification…',
    finalizingDescription: 'Gardez cette page ouverte. Cela ne prend généralement que quelques secondes.',
    unconfirmedTitle: 'Nous confirmons encore votre résultat',
    unconfirmedDescription: 'Cela prend plus de temps que d’habitude. Vous n’aurez à répéter aucune étape.',
    checkAgain: 'Vérifier à nouveau',
  },
  footer: {
    terms: 'Conditions',
    privacy: 'Confidentialité',
    biometric: 'Biométrie',
    languageLabel: 'Langue',
    copyright: '© {{year}} {{companyName}}. Tous droits réservés.',
  },
  mainScreen: {
    title: 'Vérifiez-vous anonymement',
    description:
      "Vérification d'âge rapide et privée. Nous ne collectons ni ne partageons vos données.",
    iconAlt: 'Vérification sécurisée',
    primaryAction: 'Vérifier maintenant',
    secondaryAction: 'Se connecter avec Emblem',
    separatorText: 'Déjà vérifié ?',
    missingSession:
      'La configuration de la session est manquante. Veuillez recommencer la vérification depuis le début.',
    directAccessBlocked:
      'L’accès direct n’est pas autorisé. Veuillez recommencer la vérification depuis le début.',
    emblemResumeNotice:
      "Emblem sign-in wasn't completed. You can continue with verification here.",
    emblemNoReusableNotice:
      "You don't have a reusable Emblem pass for this site yet. You can continue with verification here.",
  },
  emblemStepUp: {
    title: 'One more verification step',
    description:
      'Your Emblem passkey worked, but this site requires a higher level of verification.',
    primaryAction: 'Continue verification',
    iconAlt: 'Emblem verification step up',
    detailTitle: 'What happens next',
    detailPoint1: 'Complete a quick SafePassage verification for this site.',
    detailPoint2:
      'After you finish, your Emblem passkey will be upgraded for future L2 sites.',
    privacyHint: 'You are still verifying privately. Sites only receive the result.',
  },
  loading: {
    title: 'Mise en place du système de vérification sécurisé',
    description:
      "Vos informations personnelles ne sont jamais collectées, stockées ou partagées",
    status: {
      initializing: 'Initialisation...',
      settingUpApiKey: 'Configuration de la clé API...',
      ready: 'Prêt à commencer la vérification',
      errorRetrying: 'Erreur d’initialisation - nouvelle tentative...',
    },
    errors: {
      noApiKey: 'Aucune clé API configurée. Veuillez fournir une clé API.',
    },
  },
  cameraAccess: {
    title: 'Accès à la caméra requis',
    description:
      "L'accès à la caméra est requis pour vérifier votre âge et continuer",
    continueDesktop: 'Continuer sur ordinateur',
    separatorText: 'OU',
    noWebcam: 'Pas de webcam ?',
    privacyNotice: 'Votre image est traitée instantanément et supprimée immédiatement',
    qr: {
      title: 'Continuer sur votre téléphone',
      description: 'Scannez ce code pour terminer la vérification sur mobile',
      imageAlt: 'Code QR pour la vérification mobile',
      instruction: 'Pointez la caméra de votre téléphone vers ce code',
      waiting: 'En attente de connexion...',
      generating: 'Génération du code QR...',
      errorMissingSession: "ID de session manquant. Veuillez revenir en arrière et réessayer.",
      errorUnavailable: 'Impossible de générer le code QR. Veuillez réessayer.',
      errorFailed: 'Impossible de générer le code QR. Veuillez réessayer.',
    },
  },
  cameraSetup: {
    defaultMessage: 'Placez votre visage dans le cercle',
    warmup: {
      preparing: 'Préparation de votre vérification...',
      preparingDelayed:
        'La connexion au service de vérification prend plus de temps que prévu. Gardez cette page ouverte.',
      requesting: 'En attente de l’autorisation de la caméra...',
      initializing: 'Démarrage de la caméra...',
      positioning: 'Préparation du flux caméra...',
      delayed:
        'Cela prend plus de temps que prévu. Gardez cette page ouverte pendant le démarrage de la caméra.',
    },
    analysis: {
      title: 'Traitement de votre photo',
      description: 'Cela prend généralement quelques secondes.',
      privacy: 'Les images sont traitées de manière sécurisée et ne sont pas stockées.',
      failureMessage: "Impossible de terminer la vérification. Veuillez réessayer.",
    },
    errors: {
      stalledTitle: 'La caméra n’a pas démarré',
      interruptedTitle: 'La caméra a été interrompue',
      missingSessionId: 'ID de session manquant dans le résultat de vérification.',
      faceTimeout:
        'Délai de détection du visage dépassé. Assurez-vous que votre visage est bien visible dans la caméra.',
      faceTimeoutDetail:
        "Aucun visage n'a été détecté dans le temps imparti. Veuillez réessayer et assurez-vous que votre visage est clairement visible dans le cercle.",
    },
  },
  cameraFeedback: {
    initial_position: 'Centrez votre visage dans le cadre',
    initial_lighting: 'Trouvez un bon éclairage',
    initial_stability: 'Gardez la caméra stable',
    wide_zone_position: 'Centrez votre visage dans le cadre',
    wide_zone_center: 'Centrez votre visage dans le cadre',
    wide_too_close: 'Reculez légèrement',
    wide_off_center: 'Centrez votre visage dans le cadre',
    wide_movement: 'Restez immobile',
    wide_gaze: 'Regardez droit devant',
    wide_success: 'Bonne position !',
    zone_transition: 'Parfait ! Avancez un peu',
    great_now_move_closer: 'Parfait ! Avancez un peu',
    close_zone_position: 'Parfait ! Tenez bon',
    close_zone_gaze: 'Regardez droit devant',
    close_too_far: 'Avancez un peu',
    close_too_close: 'Reculez légèrement',
    close_off_center: 'Centrez votre visage dans le cadre',
    close_movement: 'Restez immobile',
    close_gaze: 'Regardez droit devant',
    close_success: 'Parfait ! Tenez bon',
    processing: 'Terminé ! Traitement...',
    no_face_detected: 'Aucun visage détecté',
    move_closer: 'Avancez un peu',
    move_further: 'Reculez légèrement',
    center_face: 'Centrez votre visage dans le cadre',
    look_at_camera: 'Regardez la caméra',
    improve_lighting: 'Essayons à nouveau',
    good_position: 'Très bien !',
    move_closer_to_camera: 'Avancez un peu',
    move_further_from_camera: 'Reculez légèrement',
    good_wide_position: 'Bonne position !',
    good_close_position: 'Parfait ! Tenez bon',
  },
  cameraProcessing: {
    verifying: 'Bravo ! Traitement de votre vérification',
    analyzing: 'Analyse en cours...',
  },
  idRequest: {
    l2: {
      title: "Vérification d'identité requise",
      description:
        'Veuillez fournir une pièce d’identité officielle pour terminer la vérification',
    },
    l1: {
      failed: {
        title: 'Notre scan n’a pas pu confirmer votre âge',
        description: 'Un contrôle rapide de votre ID est nécessaire pour continuer',
      },
      unavailable: {
        title: 'Terminons par un contrôle rapide de votre ID',
        description:
          'Nous n’avons pas pu vérifier votre âge à partir de votre selfie cette fois-ci, nous utiliserons donc votre pièce d’identité.',
      },
      additional: {
        title: 'Vérification supplémentaire nécessaire',
        description: 'Pour la conformité, nous devons vérifier votre âge avec un ID',
      },
    },
    primaryAction: 'Continuer',
    privacyNotice: 'Nous ne stockons ni ne partageons vos informations personnelles',
    privacyTooltip:
      "Votre pièce d’identité est utilisée uniquement pour vérifier votre âge. Elle n’est jamais enregistrée, stockée ni partagée avec le site web demandeur. Le site web demandeur reçoit uniquement une simple confirmation que vous remplissez la condition d’âge.",
    iconAlt: 'Carte d’identité',
  },
  idCapture: {
    errorTitle: 'Échec de la capture photo',
    initializingTitle: 'Initialisation de la caméra',
    initializingDescription: 'Préparation de la capture du document...',
    title: 'Prenez une photo du recto de votre ID',
    recaptureTitle: 'Nous n’avons pas pu accepter cette photo de votre ID',
    instructions:
      'Assurez-vous que votre ID est entièrement visible, avec un texte clair et lisible et tous les bords dans le cadre',
    recaptureInstructions:
      'Assurez-vous que votre ID est net et entièrement visible, puis reprenez la photo.',
    capturing: 'Capture...',
    recaptureAction: 'Reprendre la photo',
    captureAction: 'Scanner l’ID',
    capturedTitle: 'Photo capturée !',
    capturedDescription: 'Préparation de la révision...',
    cameraErrorTitle: 'Erreur de caméra',
    cameraErrorAlt: 'Erreur de caméra',
    cameraRecoveryDescription:
      'Réessayez, changez de caméra si disponible ou vérifiez que ce navigateur est autorisé à utiliser la caméra.',
    cameraPermissionHelp:
      'Sur iPhone ou iPad, vérifiez l’accès à la caméra pour Safari dans Réglages. Dans Chrome ou Safari, utilisez l’icône caméra dans la barre d’adresse pour autoriser l’accès.',
    switchCameraAction: 'Changer de caméra',
  },
  idReview: {
    title: 'Vérifiez votre ID scanné',
    description: 'Assurez-vous que toutes les informations sont clairement visibles',
    imageAlt: 'ID capturé',
    guidance: 'Votre ID doit être visible et lisible.',
    missingImage: 'Aucune image capturée. Veuillez revenir et capturer votre ID.',
    submitAction: 'Continuer',
    retakeAction: 'Reprendre',
  },
  idProcessing: {
    title: 'Vérification de votre ID',
    description:
      'Ce processus prend généralement entre 20 et 30 secondes. Dans certains cas, il peut prendre jusqu’à 60 secondes.',
    keepOpen: 'Veuillez garder cette page ouverte pendant la fin de la vérification.',
    info: 'Votre document est traité de manière sécurisée. Aucune information personnelle n’est stockée.',
    status: {
      initial: 'Vérification en cours.',
      stillWorking: 'Votre document est toujours en cours de vérification...',
      almostDone: 'Presque terminé. Merci de votre patience.',
    },
    steps: {
      received: 'Document reçu',
      analyzing: 'Analyse des informations',
      verifying: 'Vérification de l’authenticité',
      confirming: 'Confirmation de l’âge',
    },
    errors: {
      noDocumentData: 'Aucune donnée de document trouvée. Veuillez capturer votre ID à nouveau.',
      noSessionId: 'Aucun ID de session disponible. Veuillez recommencer la vérification.',
      selfieFetchFailed:
        'Impossible de récupérer le selfie depuis le serveur. Veuillez recommencer la vérification.',
      noApiKey: 'Clé API non disponible. Actualisez et recommencez la vérification.',
      generic: 'La vérification a échoué.',
    },
  },
  desktopWaiting: {
    title: 'Appareil connecté',
    description: 'Terminez la vérification sur votre appareil mobile',
    iconAlt: 'Appareil mobile',
    keepOpen: 'Gardez cette fenêtre ouverte',
    redirectInfoLine1: 'Vous serez automatiquement redirigé lorsque',
    redirectInfoLine2: 'la vérification sera terminée.',
    expiredTitle: 'Session expirée',
    expiredDescription: 'Votre session de vérification a expiré. Veuillez recommencer.',
  },
  returnToDesktop: {
    title: 'Retournez sur votre ordinateur',
    description:
      'Vérification mobile terminée. Revenez sur votre navigateur de bureau pour continuer.',
    iconAlt: 'Ordinateur de bureau',
  },
  recovery: {
    title: 'Continuer la vérification ?',
    description: 'Nous avons trouvé une session de vérification en cours',
    lastSeen: 'Dernière activité : {{screen}}',
    progressLine: '{{age}} • {{progress}}',
    progress: {
      start: 'Tout juste commencé',
      early: 'Début du processus',
      good: 'Bon progrès',
      halfway: 'Plus de la moitié',
      almost: 'Presque terminé',
      nearlyComplete: 'Quasiment terminé',
    },
    time: {
      minuteAgo: 'il y a {{count}} minute',
      minutesAgo: 'il y a {{count}} minutes',
      secondAgo: 'il y a {{count}} seconde',
      secondsAgo: 'il y a {{count}} secondes',
    },
    screens: {
      main: 'Bienvenue',
      emblemStepUp: 'Emblem Step Up',
      cameraSetup: 'Configuration de la caméra',
      verification: 'Vérification de l’âge',
      idRequest: 'ID requis',
      idCapture: 'Capture du document',
      idProcessing: 'Traitement du document',
      idReview: 'Revue de la capture',
      success: 'Vérification terminée',
      failure: 'Vérification échouée',
      error: 'Erreur technique',
      unknown: '{{screenName}}',
    },
    actions: {
      continue: 'Continuer là où je m’étais arrêté',
      startFresh: 'Recommencer',
    },
    capabilities: {
      connection: '✓ La connexion peut être rétablie',
      camera: '✓ Paramètres de caméra sauvegardés',
      manual: 'Certaines configurations devront peut-être être refaites',
    },
    info: {
      continuingLabel: 'Continuer :',
      continuingDescription: 'Reprendre la vérification là où vous vous êtes arrêté.',
      startFreshLabel: 'Recommencer :',
      startFreshDescription: 'Démarrer une nouvelle session de vérification.',
    },
  },
  errorScreen: {
    helpTitle: 'Que pouvez-vous faire :',
    invalidSession: {
      title: 'Session invalide',
      description:
        'La session de vérification est invalide ou expirée. Veuillez recommencer la vérification.',
      primaryAction: 'Contacter le support',
      suggestion1: 'Retournez sur le site du commerçant',
      suggestion2: 'Redémarrez le processus de vérification',
      suggestion3: 'Contactez le support si le problème persiste',
    },
    expiredSession: {
      title: 'Session expirée',
      description:
        'Votre session de vérification a expiré. Revenez au site ou à l’application où vous avez commencé.',
      primaryAction: 'Revenir au site',
      suggestion1: 'Revenez au site ou à l’application d’origine',
      suggestion2: 'Démarrez-y une nouvelle vérification',
      suggestion3: 'Contactez le site si vous avez besoin d’aide',
    },
    network: {
      title: 'Problème de connexion',
      description:
        'Impossible de se connecter aux services de vérification. Vérifiez votre connexion et réessayez.',
      suggestion1: 'Vérifiez votre connexion internet',
      suggestion2: 'Désactivez le VPN si vous en utilisez un',
      suggestion3: 'Essayez de recharger la page',
      suggestion4: 'Contactez le support si le problème persiste',
    },
    unsupported: {
      title: 'Navigateur non pris en charge',
      description:
        'Votre navigateur ne prend pas en charge les fonctionnalités requises pour la vérification d’âge. Utilisez un navigateur moderne comme Chrome, Firefox ou Safari.',
      primaryAction: 'Télécharger Chrome',
      suggestion1: 'Mettez à jour votre navigateur',
      suggestion2: 'Essayez Chrome, Firefox ou Safari',
      suggestion3: 'Activez JavaScript s’il est désactivé',
    },
    cameraDenied: {
      title: 'Accès à la caméra requis',
      description:
        'La vérification d’âge nécessite l’accès à la caméra. Autorisez l’accès et réessayez.',
      suggestion1: 'Cliquez sur l’icône de caméra dans la barre d’adresse',
      suggestion2: 'Sélectionnez « Autoriser » pour l’accès à la caméra',
      suggestion3: 'Actualisez la page après avoir accordé l’autorisation',
    },
    missingReturnUrl: {
      title: 'Erreur de configuration',
      description:
        'La session de vérification manque de configuration requise. Contactez le commerçant pour corriger son intégration.',
      primaryAction: 'Contacter le commerçant',
      suggestion1: 'Retournez sur le site du commerçant',
      suggestion2: 'Contactez l’équipe de support du commerçant',
      suggestion3: 'Signalez que l’intégration {{productName}} doit être mise à jour',
      suggestion4: 'Le commerçant doit fournir une URL de retour valide dans la configuration du SDK',
    },
    directAccess: {
      title: 'Accès direct interdit',
      description:
        'La vérification d’âge doit être lancée depuis le site d’un commerçant avec le SDK {{productName}}. Vous ne pouvez pas accéder à ce service directement.',
      primaryAction: 'Fermer la page',
      iconAlt: 'Accès bloqué',
      suggestion1: 'Retournez sur le site du commerçant où vous avez commencé',
      suggestion2: 'Terminez la vérification d’âge dans le processus d’achat ou d’inscription',
      suggestion3: 'Ne mettez pas cette URL en favori et ne la partagez pas',
      suggestion4: 'Chaque session de vérification est unique et doit être initiée par le commerçant',
    },
    subscriptionRequired: {
      title: 'Abonnement requis',
      description:
        'Ce compte nécessite un abonnement actif pour les vérifications en production. Contactez votre administrateur pour mettre à niveau le plan.',
      primaryAction: 'Contacter l’administrateur',
      portalAction: 'Ouvrir le portail de facturation',
    },
    planLimitReached: {
      title: 'Limite mensuelle atteinte',
      description:
        'Ce compte a atteint sa limite mensuelle de vérifications. Contactez votre administrateur pour mettre à niveau le plan ou attendez la prochaine période de facturation.',
      primaryAction: 'Contacter l’administrateur',
      portalAction: 'Ouvrir le portail de facturation',
    },
    sandboxLimitReached: {
      title: 'Limite de sandbox atteinte',
      description:
        'Ce compte a atteint sa limite de vérifications en sandbox. Demandez à un administrateur de désactiver le mode sandbox ou de mettre à niveau le plan.',
      primaryAction: 'Contacter l’administrateur',
      portalAction: 'Ouvrir le portail de facturation',
    },
  },
  failure: {
    genericError: 'La vérification a échoué',
    iconAlt: 'Vérification échouée',
    document: {
      title: 'Vérification échouée',
      description: 'Nous n’avons pas pu terminer la vérification. Essayons à nouveau.',
      reason1: 'Problèmes de qualité d’image du document',
      reason2: 'Informations pas clairement visibles',
      reason3: 'Type de document non pris en charge',
      reason4: 'Exigences de vérification d’âge non respectées',
    },
    face: {
      title: 'Vérification échouée',
      description: 'Nous n’avons pas pu terminer la vérification. Essayons à nouveau.',
      reason1: 'Le contrôle de vivacité n’a pas réussi',
      reason2: 'Mauvaises conditions de lumière',
      reason3: 'Problèmes de positionnement de la caméra',
      reason4: 'Mouvement pendant le scan',
      reason5: 'Visage pas clairement visible',
    },
    crossDeviceRestart: {
      title: 'Retournez sur votre ordinateur',
      description:
        'Cette vérification doit être redémarrée depuis le navigateur de bureau qui a affiché le code QR. Retournez-y, cliquez sur Réessayer, puis scannez le nouveau code QR avec votre téléphone.',
    },
    errors: {
      restartFailed: 'Impossible de redémarrer la vérification. Actualisez la page et réessayez.',
      switchFailed: 'Impossible de passer sur mobile. Actualisez la page et réessayez.',
    },
  },
  success: {
    title: 'Vérification d’âge réussie',
    emblemTitle: 'Compte Emblem créé !',
    emblemDescription:
      'Votre vérification d’âge portable est prête. Vous pouvez utiliser votre compte Emblem pour vérifier votre âge sur tout site compatible.',
    iconAlt: 'Succès',
    continueAction: 'Continuer vers le site',
    separatorText: 'ignorer les vérifications futures',
    createEmblemAction: 'Créer un compte Emblem',
    emblemEnrollmentComplete: 'Compte Emblem créé',
    readyDescription: 'Vous pouvez maintenant accéder à votre contenu',
    redirectingIn: 'Redirection dans {{seconds}} secondes...',
    returnUrlInvalid:
      'L’URL de retour n’est pas configurée ou est invalide. Cliquez sur Continuer pour poursuivre.',
    errors: {
      missingReturnUrl: 'URL de retour manquante - configuration SDK du commerçant incorrecte',
      emblemSignupFailed: 'Impossible de créer le compte Emblem',
    },
  },
  emblem: {
    info: {
      ariaLabel: 'En savoir plus sur Emblem',
      trigger: 'Qu’est-ce qu’Emblem ?',
      title: 'Qu’est-ce qu’Emblem ?',
      paragraph1:
        'Emblem enregistre votre vérification d’âge en toute sécurité pour vous permettre de prouver que vous avez plus de 18 ans sans partager vos données personnelles.',
      paragraph2:
        'Après avoir vérifié votre âge via {{productName}}, vous pouvez créer un compte Emblem pour éviter les vérifications futures sur les sites participants. Votre vérification reste privée ; les sites savent seulement que vous êtes vérifié.',
      linkLabel: 'Site Emblem',
    },
    signupInfo: {
      ariaLabel: 'En savoir plus sur la création d’un compte Emblem',
      trigger: 'Pourquoi créer un compte ?',
      title: 'Créer un compte Emblem',
      intro: 'Sauvegardez votre vérification pour l’avenir ! Avec un compte Emblem, vous pouvez :',
      bullet1: 'Éviter les vérifications d’âge futures sur les sites participants',
      bullet2: 'Garder vos informations personnelles privées',
      bullet3: 'Accéder instantanément au contenu restreint',
      outro:
        'Votre vérification est stockée de manière sécurisée. Les sites ne savent que vous êtes vérifié.',
      linkLabel: 'Site Emblem',
    },
  },
  mobileCompletion: {
    title: 'Vérification terminée',
    description:
      'La vérification mobile a été réalisée avec succès. Vous pouvez fermer cette fenêtre et revenir à votre ordinateur.',
    iconAlt: 'Ordinateur de bureau',
    closeWindowFallback:
      'Veuillez fermer cet onglet/cette fenêtre manuellement pour revenir à votre ordinateur.',
  },
  cameraErrorBoundary: {
    iconAlt: 'Erreur de caméra',
    trySteps: 'Essayez ces étapes :',
    skipCamera: 'Continuer sans caméra',
    permission: {
      title: 'Accès à la caméra requis',
      description: 'Pour vérifier votre âge, nous avons besoin d’accéder à votre caméra.',
      step1: 'Cliquez sur l’icône de caméra dans la barre d’adresse',
      step2: 'Sélectionnez « Autoriser » pour l’accès à la caméra',
      step3: 'Cliquez sur « Réessayer » ci-dessous',
    },
    notFound: {
      title: 'Aucune caméra détectée',
      description: 'Nous n’avons pas trouvé de caméra sur votre appareil.',
      step1: 'Vérifiez que votre caméra est correctement connectée',
      step2: 'Essayez un autre navigateur',
      step3: 'Utilisez un appareil avec caméra intégrée',
    },
    inUse: {
      title: 'Caméra utilisée',
      description: 'Votre caméra est utilisée par une autre application.',
      step1: 'Fermez les autres applications qui utilisent la caméra',
      step2: 'Vérifiez les appels vidéo ou logiciels d’enregistrement',
      step3: 'Redémarrez votre navigateur et réessayez',
    },
    unsupported: {
      title: 'Navigateur non pris en charge',
      description: 'Votre navigateur ne prend pas en charge l’accès à la caméra.',
      step1: 'Mettez à jour votre navigateur vers la dernière version',
      step2: 'Essayez Chrome, Firefox, Safari ou Edge',
      step3: 'Activez WebRTC dans les paramètres du navigateur',
    },
    default: {
      title: 'Erreur de caméra',
      description: 'Un problème est survenu lors de l’accès à votre caméra.',
      step1: 'Vérifiez la connexion de la caméra',
      step2: 'Actualisez la page et réessayez',
      step3: 'Essayez un autre navigateur',
    },
    platform: {
      chrome: 'Chrome/Edge :',
      chromeInstruction: 'Cliquez sur l’icône de caméra dans la barre d’adresse',
      firefox: 'Firefox :',
      firefoxInstruction: 'Cliquez sur l’icône des autorisations près de l’URL',
      safari: 'Safari :',
      safariInstruction: 'Allez dans Safari -> Réglages -> Sites web -> Caméra',
    },
  },
  verificationErrorBoundary: {
    iconAlt: 'Erreur',
    retryAttempt: 'Tentative {{attempt}} sur {{max}}',
    errorDetails: 'Détails de l’erreur',
    errorType: 'Type d’erreur :',
    errorTypeGeneral: 'général',
    componentStack: 'Pile de composants :',
    cameraDenied: {
      title: 'Accès à la caméra refusé',
      description:
        'Autorisez l’accès à la caméra dans les paramètres du navigateur pour continuer.',
    },
    noCamera: {
      title: 'Aucune caméra trouvée',
      description: 'Assurez-vous d’avoir une caméra fonctionnelle connectée.',
    },
    cameraError: {
      title: 'Erreur de caméra',
      description:
        'Un problème est survenu avec votre caméra. Vérifiez-la et réessayez.',
    },
    documentFailed: {
      title: 'Échec de capture du document',
      description:
        'Nous avons eu du mal à capturer votre document. Assurez une bonne lumière et réessayez.',
    },
    connectionLost: {
      title: 'Connexion perdue',
      description:
        'Nous avons perdu la connexion aux serveurs. Vérifiez votre connexion et réessayez.',
    },
    multipleErrors: {
      title: 'Plusieurs erreurs sont survenues',
      description:
        'Des problèmes persistants sont en cours. Réessayez plus tard ou contactez le support.',
    },
    default: {
      title: 'Une erreur est survenue',
      description:
        'Une erreur inattendue est survenue. Réessayez ou redémarrez si le problème persiste.',
    },
  },
  errorState: {
    title: 'Une erreur est survenue',
  },
  notifications: {
    errorTitle: 'Erreur',
    successTitle: 'Succès',
    infoTitle: 'Info',
    cameraAccessDenied:
      'L’accès à la caméra a été refusé. Accordez l’autorisation et réessayez.',
    noCameraFound: 'Aucune caméra trouvée. Assurez-vous que votre appareil en possède une.',
    cameraInUse: 'La caméra est déjà utilisée par une autre application.',
    networkError: 'Erreur réseau. Vérifiez votre connexion et réessayez.',
    unexpectedError: 'Une erreur inattendue est survenue. Veuillez réessayer.',
  },
  cameraErrors: {
    notAllowed:
      'Nous avons besoin d’accéder à la caméra pour continuer. Sélectionnez « Réessayer » pour demander l’accès. Si votre navigateur ne vous le demande plus, autorisez l’accès à la caméra pour ce site dans les paramètres de votre navigateur, puis réessayez ou rechargez la page.',
    notAllowedAction: 'Cliquez sur Autoriser lorsque l’accès à la caméra est demandé',
    security:
      'L’accès à la caméra est bloqué par les paramètres de sécurité de votre navigateur ou de votre appareil. Autorisez l’accès pour ce site dans ces paramètres, puis réessayez ou rechargez la page.',
    securityAction:
      'Autorisez l’accès à la caméra dans les paramètres de sécurité du navigateur ou de l’appareil',
    notFound: 'Aucune caméra trouvée. Assurez-vous qu’une caméra est connectée.',
    notFoundAction: 'Veuillez connecter une caméra',
    notReadable: 'La caméra est déjà utilisée par une autre application.',
    notReadableAction: 'Fermez les autres applications utilisant la caméra',
    overconstrained: 'Paramètres de caméra non pris en charge. Essai avec les paramètres par défaut...',
    overconstrainedAction: 'Nouvel essai avec les paramètres de base de la caméra',
    timeout: 'Délai d’initialisation de la caméra dépassé. Veuillez réessayer.',
    timeoutAction: 'Veuillez réessayer',
    videoStalled:
      'Votre caméra s’est allumée, mais aucune image n’est arrivée. Sélectionnez « Réessayer » pour redémarrer la caméra. Si le problème persiste, fermez les autres applications qui utilisent la caméra ou rechargez la page.',
    videoStalledAction: 'Veuillez réessayer pour redémarrer la caméra',
    interrupted:
      'Votre caméra s’est arrêtée de façon inattendue, peut-être à cause d’un appel ou d’une autre application. Sélectionnez « Réessayer » pour redémarrer la caméra.',
    interruptedAction: 'Veuillez réessayer pour redémarrer la caméra',
    initializationFailed: 'Échec de l’initialisation de la caméra. Actualisez et réessayez.',
    streamUnavailable: 'Flux caméra indisponible. Vérifiez la connexion de la caméra.',
    generic: 'Échec de l’accès à la caméra. Vérifiez la caméra et réessayez.',
    genericAction: 'Actualisez la page et réessayez',
  },
  brandHeader: {
    tagline: 'Vérification d’âge',
    merchantLabel: 'Vérification pour',
    secureLabel: 'Sécurisé',
  },
  faceMatchRecovery: {
    selfieRetry: {
      title: 'Essayons avec un nouveau selfie',
      message:
        'Nous n’avons pas pu associer votre selfie à la photo de votre ID cette fois-ci. Les photos d’identité peuvent dater de plusieurs années, et un changement d’apparence ou d’éclairage peut rendre la reconnaissance plus difficile.',
      tipsHeading: 'Quelques conseils pour votre prochain selfie :',
      tips: {
        straightOn: 'Regardez la caméra bien en face et gardez tout votre visage dans le cadre.',
        lighting: 'Placez-vous dans un endroit bien éclairé, avec la lumière face à vous.',
        expression:
          'Essayez d’adopter la même expression que sur la photo de votre ID. Si vous ne souriez pas sur la photo, gardez une expression détendue et neutre.',
        eyes: 'Assurez-vous que vos yeux sont bien visibles, sans reflets ni lunettes de soleil.',
      },
      primaryAction: 'Prendre un nouveau selfie',
      captureHeading: 'Pour votre nouveau selfie',
      captureTips: {
        straightOn: 'Regardez la caméra bien en face',
        lighting: 'Lumière face à vous',
        expression: 'Même expression que sur votre ID',
        eyes: 'Yeux bien visibles, sans lunettes de soleil',
      },
      comparing: 'Comparaison de votre nouveau selfie avec votre ID…',
    },
    repeated: {
      title: 'Toujours un problème ? Réessayons.',
      message:
        'Une photo d’identité ancienne ou un éclairage différent peut rendre la vérification plus difficile. Placez-vous face à la caméra dans un endroit bien éclairé et essayez d’adopter la même expression que sur la photo de votre ID.',
      primaryAction: 'Prendre un autre selfie',
    },
    help: {
      heading: 'Besoin d’aide ?',
      differentIdText:
        'Si vous avez une autre pièce d’identité avec une photo plus récente de vous, vous pouvez l’utiliser à la place.',
      differentIdAction: 'Utiliser une autre pièce d’identité',
    },
    differentId: {
      captureTitle: 'Prenez une photo de votre autre pièce d’identité',
      captureInstructions:
        'Prenez une photo du recto de l’autre pièce d’identité. Assurez-vous qu’elle est nette et entièrement visible.',
    },
    retainedIdUnavailable:
      'Nous avons besoin d’une nouvelle photo de votre ID pour terminer. Assurez-vous qu’elle est nette et entièrement visible.',
  },
} as const satisfies TranslationShape;
